import { useCallback, useState } from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import {
  StripeCardElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeElementChangeEvent,
} from "@stripe/stripe-js";
import { useTranslation } from "@shared/utils/internationalization";
import Text from "@ui-kit/typography/Text/Text.web";

// eslint-disable-next-line import/no-cycle
import StripeElement from "../StripeElement/StripeElement.web";
import CardBrandLogo from "../../common/CardBrandLogo/CardBrandLogo.web";

/**
 * A containing element for Stripe.JS to inject their elements into
 */
function StripeCardNumberElement({
  onValidField,
  onInvalidField,
}: {
  /**
   * Fires when a value is well-formed and potentially complete.
   * It is not an indicator of whether a customer is done with their input --
   * it only indicates that the Element contains a potentially complete, well-formed value.
   */
  onValidField?: () => void;
  /**
   * Fires when a value is deemed invalid
   */
  onInvalidField?: () => void;
}) {
  const { t } = useTranslation("checkout");

  const [brand, setBrand] =
    useState<StripeCardNumberElementChangeEvent["brand"]>("unknown");
  const [error, setError] = useState("");

  const handleChange = useCallback(
    (evt: StripeElementChangeEvent) => {
      // Event handling is unique per element
      if (evt.elementType === "cardNumber") {
        const cardEvt = evt as StripeCardElementChangeEvent;
        setBrand(cardEvt.brand || "unknown");
      }

      if (evt.error) {
        setError(evt.error.message);
      } else if (!evt.error) {
        setError("");
      }

      if (evt.complete) {
        onValidField?.();
      } else {
        onInvalidField?.();
      }
    },
    [onValidField, onInvalidField]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="sm">
          {t("Card number")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={!error ? "border" : "red.600"}
        borderRadius="sm"
        px="sm"
        bg="bg"
        position="relative"
      >
        <Box width="calc(100% - 24px)" paddingRight="sm">
          <StripeElement
            element="cardNumber"
            onChange={handleChange}
            elementProps={{
              placeholder: t("Card number"),
              disableLink: true,
            }}
          />
        </Box>
        <Box
          overflow="hidden"
          position="absolute"
          top={0}
          right={0}
          width="element.4xl"
          height="100%"
          display="flex"
          alignItems="center"
          paddingLeft="sm"
          paddingRight="sm"
        >
          {brand && brand !== "unknown" ? (
            <Box
              animation="slideLeft 0.5s forwards"
              data-testid="stripe-element-cardNumber-brand-logo-container"
            >
              <CardBrandLogo brand={brand} />
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={2}
          data-testid="stripe-element-cardNumber-error"
        >
          {error}
        </Text>
      </Box>
    </Box>
  );
}

export default StripeCardNumberElement;
